/**
 * Everything entered after the fact, and how late.
 *
 * Back-dating is necessary here — 31 sites, paperwork arriving in bundles, and
 * not enough people to key it the day it happens. But an ERP that lets anybody
 * write into last quarter without anybody noticing is an ERP nobody should
 * trust. This screen is the other half of that bargain: the entry is allowed,
 * and it is visible.
 *
 * Nothing is stored to build this. It compares each document's own date with
 * the moment it was keyed, so it can never drift out of step with reality.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, Clock } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Loading, SearchInput, Select, Stat,
  Th, sortRows, useSort,
} from '@/components/ui'
import { usePeople, useQuery, useSites } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, dateTime } from '@/lib/format'
import { useCompanySettings } from '@/lib/settings'

const TYPES = [
  { value: 'INDENT', label: 'Site indents' },
  { value: 'PR', label: 'Purchase requisitions' },
  { value: 'PO', label: 'Purchase orders' },
  { value: 'GRN', label: 'Goods receipts' },
  { value: 'MIN', label: 'Material issues' },
  { value: 'MRN', label: 'Material returns' },
  { value: 'IST', label: 'Inter-site transfers' },
  { value: 'INVOICE', label: 'Vendor invoices' },
  { value: 'PAYMENT', label: 'Payments' },
  { value: 'CREDIT_NOTE', label: 'Credit notes' },
]

const ROUTE: Record<string, string> = {
  INDENT: '/indents', PR: '/prs', PO: '/pos', GRN: '/grns',
  MIN: '/mins', MRN: '/mrns', IST: '/ists', INVOICE: '/invoices',
}

export function LateEntries() {
  // Names, not email addresses — management 01-09-2026. See src/lib/people.ts.
  const people = usePeople()
  const [type, setType] = useState('')
  const [site, setSite] = useState('')
  const [minDays, setMinDays] = useState('1')
  const [q, setQ] = useState('')
  const { data: sites } = useSites()
  const { sort, toggle } = useSort('days_late')
  // 0212: amber and red limits are System Settings (30 and 90 until changed).
  const { lateEntryWarnDays: warn, lateEntrySeriousDays: serious } = useCompanySettings()

  const { data: rows, loading } = useQuery<any>('v_backdated_documents', () => {
    let s = supabase.from('v_backdated_documents').select('*')
    if (type) s = s.eq('doc_type', type)
    if (site) s = s.eq('site_id', site)
    if (Number(minDays) > 1) s = s.gte('days_late', Number(minDays))
    return s.order('days_late', { ascending: false }).limit(1000)
  }, [type, site, minDays], { cacheable: false })

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    if (!t) return rows
    return rows.filter((r: any) => [r.id, r.site_name, r.entered_by_name, r.entered_by_email]
      .some((v) => String(v ?? '').toLowerCase().includes(t)))
  }, [rows, q])

  const sorted = useMemo(() => sortRows(filtered, sort, {
    days_late: 'number', doc_date: 'date', created_at: 'date',
  }), [filtered, sort])

  const stats = useMemo(() => {
    const late = filtered.map((r: any) => Number(r.days_late))
    return {
      count: late.length,
      worst: late.length ? Math.max(...late) : 0,
      average: late.length ? Math.round(late.reduce((a: number, b: number) => a + b, 0) / late.length) : 0,
      overMonth: late.filter((d: number) => d > warn).length,
    }
  }, [filtered, warn])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-bold text-slate-800">Late entries</h1>
        <span className="pill-grey">{filtered.length}</span>
      </div>

      <Alert tone="info" title="Why this screen exists">
        Documents may be dated up to three years back, because with 31 sites the paperwork
        rarely reaches a keyboard the same day. This is the register of every document keyed
        after the event, so late entry is a thing the company can <strong>see</strong> rather than
        something that quietly happens. A few days late is normal. A receipt keyed four months
        late is worth a conversation.
      </Alert>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Late entries" value={stats.count} icon={<CalendarClock className="h-5 w-5" />} />
        <Stat label="Average delay" value={`${stats.average} days`} />
        <Stat label="Worst" value={`${stats.worst} days`} tone={stats.worst > serious ? 'bad' : 'warn'} />
        <Stat label={warn === 30 ? 'Over a month late' : `Over ${warn} days late`} value={stats.overMonth}
              tone={stats.overMonth > 0 ? 'warn' : 'good'} />
      </div>

      <Card title="Documents entered after the event"
            subtitle="Comparing each document's own date with the moment it was keyed">
        <div className="mb-3 grid gap-3 sm:grid-cols-4">
          <Field label="Document type">
            <Select value={type} onChange={(e) => setType(e.target.value)} placeholder="All types"
                    options={TYPES} />
          </Field>
          <Field label="Site">
            <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="At least this late">
            <Select value={minDays} onChange={(e) => setMinDays(e.target.value)}
                    options={[
                      { value: '1', label: '1 day' },
                      { value: '7', label: 'a week' },
                      { value: '30', label: 'a month' },
                      { value: '90', label: 'three months' },
                    ]} />
          </Field>
          <Field label="Search">
            <SearchInput value={q} onChange={setQ} placeholder="Document number, site, person…" />
          </Field>
        </div>

        {loading && <Loading />}
        {!loading && sorted.length === 0 && (
          <EmptyState icon={<Clock className="h-8 w-8" />} title="Nothing entered late"
                      message="Every document was keyed on the day it happened." />
        )}
        {!loading && sorted.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="doc_type" sort={sort} onSort={toggle}>Type</Th>
                  <Th id="id" sort={sort} onSort={toggle}>Document</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="doc_date" sort={sort} onSort={toggle}>Document date</Th>
                  <Th id="created_at" sort={sort} onSort={toggle}>Keyed on</Th>
                  <Th id="days_late" sort={sort} onSort={toggle} className="num" align="right">Days late</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  <Th id="entered_by_name" sort={sort} onSort={toggle}>Entered by</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r: any) => {
                  const d = Number(r.days_late)
                  return (
                    <tr key={`${r.doc_type}-${r.id}`} className={d > serious ? 'bg-red-50/50' : ''}>
                      <td className="text-xs font-semibold text-slate-500">{r.doc_type}</td>
                      <td className="font-mono text-xs">
                        {ROUTE[r.doc_type]
                          ? <Link to={`${ROUTE[r.doc_type]}/${r.id}`} className="text-oval-700 hover:underline">{r.id}</Link>
                          : r.id}
                      </td>
                      <td className="text-xs">{r.site_name ?? '—'}</td>
                      <td className="text-xs">{ddmmyyyy(r.doc_date)}</td>
                      <td className="text-xs text-slate-500">{dateTime(r.created_at)}</td>
                      <td className={`num text-sm font-bold ${
                        d > serious ? 'text-red-600' : d > warn ? 'text-amber-700' : 'text-slate-700'}`}>
                        {d}
                      </td>
                      <td className="text-xs">{r.status}</td>
                      <td className="max-w-[180px] truncate text-xs text-slate-500">
                        {r.entered_by_name ?? people.name({ email: r.entered_by_email })}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
